// Danh sách IMEI của MỘT sản phẩm (mọi variant, mọi kho) — mở từ nút
// "Danh sách IMEI" trong modal Lịch sử xuất nhập kho ở màn Sản phẩm.
// Mặc định chỉ hiện hàng TỒN KHO; gạt sang "Tất cả" để xem cả máy đã bán.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, Loader2, Download, Search } from "lucide-react";
import { apiClient } from "@/lib/api/client";
import { Button } from "@/components/ui/button";
import { Table, THead, TBody, Tr, Th, Td } from "@/components/ui/table";
import { SerialHistoryModal } from "@/features/inventory/serial-history-modal";

interface SerialRow {
  id: string;
  serialNo: string;
  status: string;
  variantSku: string | null;
  currentWarehouseName?: string | null;
}

const STATUS_LABELS: Record<string, string> = {
  imported: "Nhập về", in_stock: "Tồn kho", sold: "Đã bán", in_repair: "Đang sửa",
  rma_to_vendor: "RMA nhà cung cấp", defective: "Hỏng", returned: "Hoàn trả",
};

function statusBadge(status: string) {
  const cls =
    status === "in_stock"
      ? "bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-300"
      : status === "sold"
        ? "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"
        : "bg-muted text-muted-foreground";
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${cls}`}>
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

interface Props {
  productId: string;
  productName: string;
  onClose: () => void;
}

export function ProductSerialsModal({ productId, productName, onClose }: Props) {
  const [historySerial, setHistorySerial] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [onlyInStock, setOnlyInStock] = useState(true);

  const { data: serials = [], isLoading } = useQuery({
    queryKey: ["product-serials", productId, onlyInStock],
    queryFn: () =>
      apiClient
        .get<{ data: SerialRow[] }>("/v1/serials", {
          params: {
            product_id: productId,
            ...(onlyInStock ? { status: "in_stock" } : {}),
            limit: 500,
          },
        })
        .then((r) => r.data.data ?? []),
  });

  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? serials.filter(
        (s) =>
          s.serialNo.toLowerCase().includes(needle) ||
          (s.variantSku ?? "").toLowerCase().includes(needle),
      )
    : serials;

  function exportExcel() {
    const header = ["STT", "IMEI / Serial", "SKU", "Tên sản phẩm", "Kho", "Trạng thái"];
    const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [header.map(esc).join(",")];
    filtered.forEach((s, i) => {
      lines.push(
        [i + 1, s.serialNo, s.variantSku ?? "", productName, s.currentWarehouseName ?? "", STATUS_LABELS[s.status] ?? s.status]
          .map(esc)
          .join(","),
      );
    });
    // ﻿ (BOM) so Excel reads UTF-8 Vietnamese correctly; CRLF line endings.
    const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `IMEI-${productName.replace(/[^\p{L}\p{N}]+/gu, "-").slice(0, 60)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-background">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 border-b px-4 py-3 sm:px-6">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">Danh sách IMEI</h2>
          <p className="truncate text-sm text-muted-foreground">{productName}</p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportExcel} disabled={filtered.length === 0}>
            <Download size={15} className="mr-1" /> Xuất Excel
          </Button>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Đóng">
            <X size={18} />
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 border-b px-4 py-2 sm:px-6">
        <div className="relative min-w-[220px] max-w-sm flex-1">
          <Search size={16} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Lọc nhanh IMEI / SKU…"
            className="h-9 w-full rounded-md border border-input bg-background pl-8 pr-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>
        <div className="flex rounded-md border p-0.5 text-sm">
          <button
            type="button"
            onClick={() => setOnlyInStock(true)}
            className={`rounded px-2.5 py-1 ${onlyInStock ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
          >
            Còn tồn
          </button>
          <button
            type="button"
            onClick={() => setOnlyInStock(false)}
            className={`rounded px-2.5 py-1 ${!onlyInStock ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
          >
            Tất cả
          </button>
        </div>
        <span className="text-sm text-muted-foreground">
          {filtered.length}
          {needle ? `/${serials.length}` : ""} IMEI
        </span>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 py-3 sm:px-6">
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
            <Loader2 size={18} className="animate-spin" /> Đang tải…
          </div>
        ) : serials.length === 0 ? (
          <p className="py-16 text-center text-sm text-muted-foreground">
            {onlyInStock
              ? "Không có IMEI tồn kho — thử gạt sang \"Tất cả\" để xem cả máy đã bán."
              : "Sản phẩm này chưa có IMEI nào (không quản lý theo serial, hoặc chưa nhập)."}
          </p>
        ) : (
          <Table>
            <THead>
              <Tr>
                <Th className="w-12 text-right">#</Th>
                <Th>IMEI / Serial</Th>
                <Th>SKU</Th>
                <Th>Kho</Th>
                <Th>Trạng thái</Th>
                <Th className="text-right">Thao tác</Th>
              </Tr>
            </THead>
            <TBody>
              {filtered.map((s, i) => (
                <Tr key={s.id}>
                  <Td className="text-right text-muted-foreground">{i + 1}</Td>
                  <Td>
                    <button
                      type="button"
                      onClick={() => setHistorySerial(s.serialNo)}
                      className="font-mono text-sm text-primary underline-offset-2 hover:underline"
                    >
                      {s.serialNo}
                    </button>
                  </Td>
                  <Td className="font-mono text-xs">{s.variantSku ?? "—"}</Td>
                  <Td className="whitespace-nowrap">{s.currentWarehouseName ?? "—"}</Td>
                  <Td>{statusBadge(s.status)}</Td>
                  <Td className="text-right">
                    <button
                      type="button"
                      onClick={() => setHistorySerial(s.serialNo)}
                      className="text-sm text-primary hover:underline"
                    >
                      Xem lịch sử →
                    </button>
                  </Td>
                </Tr>
              ))}
              {filtered.length === 0 && (
                <Tr>
                  <Td colSpan={6} className="text-center text-muted-foreground">Không có IMEI khớp bộ lọc</Td>
                </Tr>
              )}
            </TBody>
          </Table>
        )}
      </div>

      {historySerial && (
        <SerialHistoryModal serialNo={historySerial} onClose={() => setHistorySerial(null)} />
      )}
    </div>
  );
}
